/**
 * AdminView.jsx — Rê Leve IA (Etapa 12)
 *
 * Área administrativa protegida por senha. Acessível via ?mode=admin.
 * A senha é definida em VITE_ADMIN_PASSWORD (não é segredo de alto valor —
 * é só uma barreira simples contra acesso casual).
 *
 * Mostra:
 *  - Health completo da API
 *  - Lista de eventos suportados pelo n8n
 *  - Status de cada serviço (LLM, RAG, n8n, Stripe, Neon)
 *  - Logs locais (sessões, pagamentos, conversa)
 *
 * REGRA: nunca exibe chaves, URLs de webhook completas, ou valores secretos.
 */

import { useEffect, useState } from 'react';
import { apiHealth, isApiEnabled, apiBaseUrl } from '../lib/apiClient';

const ADMIN_PASSWORD = (() => {
  try {
    return import.meta.env.VITE_ADMIN_PASSWORD || 'releve-admin-2026';
  } catch {
    return 'releve-admin-2026';
  }
})();

function readSavedAuth() {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem('releve-admin-auth') === 'true';
}

function PasswordGate({ onSuccess }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');

  function tryAuth() {
    if (pw === ADMIN_PASSWORD) {
      sessionStorage.setItem('releve-admin-auth', 'true');
      onSuccess();
    } else {
      setError('Senha incorreta');
      setPw('');
    }
  }

  return (
    <div style={{ maxWidth: 400, margin: '60px auto', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>🔒</div>
      <h2 style={{ margin: '0 0 8px' }}>Área Administrativa</h2>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
        Acesso restrito. Informe a senha para continuar.
      </p>
      <input
        type="password"
        value={pw}
        onChange={e => { setPw(e.target.value); setError(''); }}
        onKeyDown={e => e.key === 'Enter' && tryAuth()}
        placeholder="Senha"
        style={{
          width: '100%', padding: '10px 14px', fontSize: 14,
          border: '1px solid #d1d5db', borderRadius: 8, marginBottom: 12,
        }}
        autoFocus
      />
      <button
        onClick={tryAuth}
        disabled={!pw}
        style={{
          width: '100%', padding: '10px 14px',
          background: pw ? '#065f46' : '#94a3b8',
          color: '#fff', border: 'none', borderRadius: 8,
          fontSize: 14, fontWeight: 600,
          cursor: pw ? 'pointer' : 'not-allowed',
        }}
      >
        Entrar
      </button>
      {error && (
        <p style={{ color: '#dc2626', fontSize: 12, marginTop: 12 }}>{error}</p>
      )}
      <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 20 }}>
        Dica: a senha está em <code>VITE_ADMIN_PASSWORD</code>.<br/>
        Padrão: <code>releve-admin-2026</code>.
      </p>
    </div>
  );
}

function StatusCard({ title, ok, label, details = [] }) {
  return (
    <div style={{
      background: '#fff', border: `1px solid ${ok ? '#bbf7d0' : '#fde68a'}`,
      borderRadius: 8, padding: 12, marginBottom: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 20 }}>{ok ? '✅' : '⚪'}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{label}</div>
        </div>
      </div>
      {details.length > 0 && (
        <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 11, color: '#475569' }}>
          {details.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
    </div>
  );
}

function HealthDashboard() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  async function reload() {
    setLoading(true);
    const h = await apiHealth();
    setHealth(h);
    setLoading(false);
  }

  useEffect(() => { if (isApiEnabled) reload(); }, []);

  if (!isApiEnabled) {
    return (
      <div style={{ padding: 12, color: '#92400e', background: '#fef3c7', borderRadius: 8 }}>
        API real desativada (modo demo local).
      </div>
    );
  }

  if (!health) {
    return (
      <div style={{ padding: 12 }}>
        {loading ? 'Carregando...' : 'Sem dados — API pode estar dormindo.'}
        <button onClick={reload} style={{ marginLeft: 8 }}>Tentar novamente</button>
      </div>
    );
  }

  const llm = health.llm || {};
  const n8n = health.n8n || {};
  const rag = health.rag || {};
  const payments = health.payments || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Status dos Serviços</h3>
        <button onClick={reload} disabled={loading} style={{ padding: '4px 12px' }}>
          {loading ? '...' : '🔄 Atualizar'}
        </button>
      </div>

      <StatusCard
        title="API Backend"
        ok={health.status === 'ok'}
        label={`mode: ${health.mode} | database: ${health.database}`}
        details={[`Base URL: ${apiBaseUrl}`]}
      />
      <StatusCard
        title="LLM (OpenAI)"
        ok={llm.enabled}
        label={`${llm.mode || 'desconhecido'} — model: ${llm.model || '-'}`}
        details={[
          `Provider: ${llm.provider || '-'}`,
          `OpenAI configurada: ${llm.openaiConfigured ? 'sim' : 'não'}`,
        ]}
      />
      <StatusCard
        title="Harness (Pre + Post Guardrails)"
        ok={!!health.harness?.enabled}
        label="Sempre ativo (não pode ser desligado)"
        details={[
          `Pré-guardrails: ${health.harness?.preGuardrails ? 'ativo' : 'inativo'}`,
          `Pós-guardrails: ${health.harness?.postGuardrails ? 'ativo' : 'inativo'}`,
          `Fallback: ${health.harness?.fallback ? 'ativo' : 'inativo'}`,
        ]}
      />
      <StatusCard
        title="RAG (pgvector)"
        ok={rag.enabled}
        label={rag.mode || 'desconhecido'}
        details={[
          `Embedding model: ${rag.embeddingModel || '-'}`,
          `Top-K: ${rag.topK || '-'}`,
        ]}
      />
      <StatusCard
        title="FAQ Chatbot"
        ok={!!health.faq?.enabled}
        label={health.faq?.mode || '-'}
      />
      <StatusCard
        title="n8n (Orquestração)"
        ok={n8n.enabled}
        label={n8n.mode || 'desconhecido'}
        details={[`Webhook configurado: ${n8n.configured ? 'sim' : 'não'}`]}
      />
      <StatusCard
        title="Pagamentos (Stripe)"
        ok={payments.enabled}
        label={`${payments.mode || '-'} | provider: ${payments.provider || '-'}`}
        details={[
          `Stripe configurado: ${payments.stripeConfigured ? 'sim' : 'não (simulado)'}`,
          `Webhook configurado: ${payments.webhookConfigured ? 'sim' : 'não'}`,
        ]}
      />

      <details style={{ marginTop: 16 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: '#6b7280' }}>
          Ver JSON completo do /health
        </summary>
        <pre style={{
          background: '#1e293b', color: '#e2e8f0',
          padding: 12, borderRadius: 8, fontSize: 11,
          marginTop: 8, overflowX: 'auto',
        }}>{JSON.stringify(health, null, 2)}</pre>
      </details>
    </div>
  );
}

function LocalDataPanel() {
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (typeof localStorage === 'undefined') return;
    const keys = Object.keys(localStorage).filter(k => k.startsWith('releve'));
    const sample = {};
    keys.forEach(k => {
      try {
        const val = JSON.parse(localStorage.getItem(k));
        sample[k] = Array.isArray(val) ? `${val.length} item(s)` :
          (typeof val === 'object' ? Object.keys(val).length + ' campo(s)' :
            String(val).slice(0, 30));
      } catch {
        sample[k] = '(não JSON)';
      }
    });
    setStats({ totalKeys: keys.length, sample });
  }, []);

  return (
    <div>
      <h3 style={{ marginTop: 0 }}>Dados Locais (sessionStorage / localStorage)</h3>
      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
        Total de chaves Rê Leve: <strong>{stats.totalKeys || 0}</strong>
      </p>
      {stats.sample && Object.keys(stats.sample).length > 0 ? (
        <table style={{ width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f3f4f6' }}>
              <th style={{ padding: 6, textAlign: 'left' }}>Chave</th>
              <th style={{ padding: 6, textAlign: 'left' }}>Conteúdo</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(stats.sample).map(([k, v]) => (
              <tr key={k} style={{ borderBottom: '1px solid #e5e7eb' }}>
                <td style={{ padding: 6, fontFamily: 'monospace', fontSize: 11 }}>{k}</td>
                <td style={{ padding: 6 }}>{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ fontSize: 12, color: '#94a3b8' }}>Nenhum dado local ainda.</p>
      )}
      <button
        onClick={() => {
          if (confirm('Limpar TODOS os dados Rê Leve do navegador?')) {
            Object.keys(localStorage)
              .filter(k => k.startsWith('releve'))
              .forEach(k => localStorage.removeItem(k));
            window.location.reload();
          }
        }}
        style={{
          marginTop: 12, padding: '6px 12px',
          background: '#dc2626', color: '#fff',
          border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12,
        }}
      >
        🗑 Limpar todos os dados locais
      </button>
    </div>
  );
}

export default function AdminView({ onExit }) {
  const [authed, setAuthed] = useState(readSavedAuth);

  if (!authed) {
    return <PasswordGate onSuccess={() => setAuthed(true)} />;
  }

  function logout() {
    sessionStorage.removeItem('releve-admin-auth');
    onExit && onExit();
  }

  return (
    <div style={{ maxWidth: 1100, margin: '20px auto', padding: '0 16px' }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', background: '#1e293b', color: '#fff',
        borderRadius: 8, marginBottom: 16,
      }}>
        <div>
          <strong>🔒 Área Administrativa — Rê Leve IA</strong>
          <p style={{ margin: 0, fontSize: 12, opacity: 0.8 }}>
            Visualização técnica para administradores
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onExit} style={btnStyle}>
            🛍️ Voltar ao site
          </button>
          <button onClick={logout} style={{ ...btnStyle, background: '#dc2626' }}>
            🚪 Sair
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <section style={sectionStyle}>
          <HealthDashboard />
        </section>
        <section style={sectionStyle}>
          <LocalDataPanel />
        </section>
      </div>

      <section style={{ ...sectionStyle, marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>Atalhos úteis</h3>
        <ul style={{ fontSize: 13, lineHeight: 1.8 }}>
          <li><a href="?mode=client">Site do Cliente</a></li>
          <li><a href="?mode=developer">Developer Lab</a></li>
          <li><a href="https://releve-api.onrender.com/health" target="_blank" rel="noreferrer">/health (API)</a></li>
          <li><a href="https://dashboard.render.com" target="_blank" rel="noreferrer">Render Dashboard</a></li>
        </ul>
      </section>
    </div>
  );
}

const btnStyle = {
  padding: '6px 12px',
  background: '#064e3b',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
};

const sectionStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: 16,
};
